import type { ReactNode } from "react";
import { LoadingOutlined, RightOutlined } from "@ant-design/icons";
import { KbdChip } from "./modalPrimitives";

/**
 * Един ред в избор „кое от двете" — „Депозит" (`DepositChoiceModal`) и
 * „Фактуриране" (`InvoicingChoiceModal`): икона, надпис отгоре (откъде/какво),
 * заглавие, едно изречение пояснение. Неактивен — с причината в червено под
 * него (`blockedReason`) или със „проверява се" (`pendingText`, сиво, със
 * спинър), докато облакът отговори. ⏎ стои на реда под клавиатурния курсор.
 */
export function ChoiceRow({
  icon,
  kicker,
  title,
  description,
  highlighted,
  showEnterHint,
  blockedReason = null,
  pendingText = null,
  isNarrow,
  onClick,
}: {
  icon: ReactNode;
  /** Надписът над заглавието — „Гостът плаща сега" / „Фискално устройство". */
  kicker: string;
  title: string;
  description: string;
  highlighted: boolean;
  /** ⏎ горе вдясно — редът под клавиатурния курсор (само десктоп). */
  showEnterHint: boolean;
  blockedReason?: string | null;
  /** Редът чака отговор (не е отказан) — сиво, не червено. */
  pendingText?: string | null;
  isNarrow: boolean;
  onClick: () => void;
}) {
  const disabled = !!blockedReason || !!pendingText;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative flex w-full cursor-pointer items-center rounded-2xl border text-left transition-colors disabled:cursor-not-allowed ${
        isNarrow ? "gap-3 p-3.5" : "gap-4 p-4"
      } ${
        highlighted
          ? "border-pos-accent bg-pos-marked"
          : "border-pos-line bg-pos-card enabled:hover:border-pos-accent/50 enabled:hover:bg-pos-marked"
      }`}
    >
      <span
        className={`grid shrink-0 place-items-center rounded-xl bg-pos-ready-bg text-pos-accent ${
          isNarrow ? "size-11 text-[20px]" : "size-12 text-[22px]"
        } ${disabled ? "opacity-55" : ""}`}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={`flex flex-col gap-0.5 ${disabled ? "opacity-55" : ""}`}>
          {/* Кирилица — с текстовия шрифт, не с моноширинния */}
          <span className="text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1.2px] text-pos-sec-head">
            {kicker}
          </span>
          <span
            className={`font-extrabold leading-[1.2] text-pos-ink ${
              isNarrow ? "text-base" : "text-[17px]"
            }`}
          >
            {title}
          </span>
          <span className="text-[13px] font-medium leading-snug text-pos-soft2">{description}</span>
        </span>
        {blockedReason ? (
          <span className="mt-1.5 text-[12.5px] font-semibold leading-snug text-pos-danger-ink">
            {blockedReason}
          </span>
        ) : pendingText ? (
          <span className="mt-1.5 flex items-center gap-1.5 text-[12.5px] font-medium leading-snug text-pos-muted">
            <LoadingOutlined className="shrink-0" />
            {pendingText}
          </span>
        ) : null}
      </span>
      {!disabled && (
        // `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите.
        <RightOutlined className="shrink-0 text-[13px] !text-pos-accent" />
      )}
      {showEnterHint && <KbdChip shortcut="⏎" colorClass="text-pos-faint" />}
    </button>
  );
}
